import { CacheKey, SyncStatusCode, validateDevice, validateEventListResponseForAllDisplays, } from '@signage-player/data-store';
import { CacheStoreTag } from '@signage-player/cache';
import { DownloadResultCode, HealthcheckType, } from '@signage-player/contracts';
import { ContentSyncManager } from '@signage-player/content-sync';
import { EventListService, sanitizeForLogging, isNetworkError, monotonicMs, jitter } from '@signage-player/shared-logic';
import { Scheduler } from './Scheduler';
import { buildScreenListPayload } from './ScreenListPayload';
export var Platform;
(function (Platform) {
    Platform["ElectronMint"] = "electron-mint";
    Platform["ElectronUbuntu"] = "electron-ubuntu";
    Platform["ElectronUbuntuArm"] = "electron-ubuntu-rpi";
    Platform["ElectronWindows"] = "electron-windows";
    Platform["ElectronUnknown"] = "electron-unknown";
    Platform["Web"] = "web";
    Platform["WebPwa"] = "web-pwa";
})(Platform || (Platform = {}));
export class SignageApplication {
    constructor(logger, config, displayPlayerManager, mediaControl, cacheManager, authInstance, apiService, lifecycle, deviceService, screenService, multiInstanceManager, processActionService, screenVendor, healthcheckManager, platform) {
        this.logger = logger;
        this.config = config;
        this.displayPlayerManager = displayPlayerManager;
        this.mediaControl = mediaControl;
        this.cacheManager = cacheManager;
        this.authInstance = authInstance;
        this.apiService = apiService;
        this.lifecycle = lifecycle;
        this.deviceService = deviceService;
        this.screenService = screenService;
        this.multiInstanceManager = multiInstanceManager;
        this.processActionService = processActionService;
        this.screenVendor = screenVendor;
        this.healthcheckManager = healthcheckManager;
        this.platform = platform;
        this.eventList = null;
        this.eventListLoadedForDisplayIds = [];
        this.device = null;
        this.mainLoopInterval = null;
        this.monitoringInterval = null;
        this.monitoringIndex = 0;
        this.emergencyGeneratorInterval = null;
        /** Pending retry when a received event list does not cover every device display (e.g. backend EL-generation queue lag). */
        this.incompleteEventListRetryTimer = null;
        this.dateDiff = null;
        this.offlineMode = false;
        /** Wall-clock timestamp (ms) when offline mode began — reported to server. */
        this.offlineSince = null;
        /** Monotonic ms when offline mode began — used for duration math (NTP-jump-safe). */
        this.offlineSinceMono = null;
        this.destroyPromise = null;
        /** Monotonic ms of last screen-vendor restart (NTP-jump-safe interval check). */
        this.lastScreenVendorRestartMono = null;
        /** Number of consecutive failed screen-vendor restart attempts; drives backoff so a broken vendor doesn't retry every tick. */
        this.screenVendorRestartFailureCount = 0;
        /** Monotonic ms before which we won't attempt another restart after a failure. */
        this.screenVendorRestartNextAttemptMono = 0;
        /**
         * Per-playable download-failure backoff. Throttles the retry/log cadence for a playable that
         * can't be fetched (missing media variant, 404, unreachable CDN) so it doesn't re-attempt and
         * error-log on every 1s mainLoop tick — but NEVER gives up (caps the interval, retries forever,
         * like the websocket reconnect), so content that appears later still downloads.
         */
        this.downloadFailureBackoff = new Map();
        this.lastStatusFromContentSyncManager = null;
        this.schedulerForDisplay = new Map();
        this.displayPlayerManagerInitializedOnDisplay = null;
        /** Monotonic ms of last main-loop healthcheck ping. */
        this.lastPlayHealthcheckPingMono = null;
        /** Monotonic ms of last periodic app-status log. */
        this.lastAppStatusLogMono = null;
        this.displayImminence = new Map();
        this.contentSyncManager = new ContentSyncManager(this.config, this.logger, this.authInstance, this.apiService, this.cacheManager);
    }
    //#region Lifecycle
    initialize() {
        this.logger.debug('[SignageApplication] Initializing signage application...');
        this.contentSyncManager.initializeDeviceSync(device => this.handleNewDeviceData(device));
        this.contentSyncManager.initializeEventlistSync(eventList => this.handleNewEventList(eventList));
        if (this.config.dynamicConfig.timeSyncAdapter) {
            this.contentSyncManager.initializeTimeSync(currentTime => this.handleNewTimeData(currentTime));
        }
        this.contentSyncManager.initializeStatusHandler(status => this.handleStatus(status));
        this.contentSyncManager.initializeLowBandwidthModeHandler(enabled => this.handleLowBandwidthModeChange(enabled));
        this.contentSyncManager.startContentSyncing();
        this.displayPlayerManager.onDestroy(displayId => {
            if (this.schedulerForDisplay.has(displayId)) {
                this.schedulerForDisplay.delete(displayId);
                this.logger.debug('[SignageApplication] Scheduler for display deleted', { displayId });
            }
        });
        this.mainLoopInterval = setInterval(() => {
            this.mainLoop();
        }, 1000);
        // Stagger the first monitoring tick so a fleet-wide firmware-update boot
        // doesn't fire `processDeviceData` and `restartScreens` on every device at
        // boot+2.5s simultaneously.
        setTimeout(() => {
            this.monitoringInterval = setInterval(() => {
                this.monitoringLoop();
            }, 60000);
            this.monitoringLoop();
            // ideally to not colidate with the main loop + wait a while because the startup might a bit slow
        }, jitter(2500));
        this.updateLoggerAdditionalContext();
        /*this.apiService.getEventlistsForAllDisplays().then(eventlists => {
          this.logger.notice('All eventlists fetched', {eventlists});
        }).catch(error => {
          this.logger.error('Error fetching all eventlists', {error});
        });*/
    }
    async destroy() {
        if (this.destroyPromise) {
            return this.destroyPromise;
        }
        this.destroyPromise = new Promise((resolve, reject) => {
            try {
                this.logger.debug('[SignageApplication] Destroying signage application...');
                if (this.mainLoopInterval) {
                    clearInterval(this.mainLoopInterval);
                    this.mainLoopInterval = null;
                }
                if (this.monitoringInterval) {
                    clearInterval(this.monitoringInterval);
                    this.monitoringInterval = null;
                }
                if (this.emergencyGeneratorInterval) {
                    clearInterval(this.emergencyGeneratorInterval);
                    this.emergencyGeneratorInterval = null;
                }
                this.cancelIncompleteEventListRetry();
                Promise.allSettled([this.contentSyncManager.destroy(), this.displayPlayerManager.destroy(), this.mediaControl.destroy()]).then(async (results) => {
                    const failed = results.filter(r => r.status === 'rejected');
                    try {
                        if (failed.length > 0) {
                            const error = failed[0].reason;
                            console.error('SignageApplication destroyed with error:', error);
                            this.logger.error('SignageApplication destroyed with error:', { error });
                            await this.logger.destroy();
                            reject(error);
                        }
                        else {
                            console.log('SignageApplication destroyed successfully');
                            await this.logger.destroy();
                            resolve();
                        }
                    }
                    catch (innerError) {
                        console.error('SignageApplication destroyed with error:', innerError);
                        reject(innerError);
                    }
                });
            }
            catch (error) {
                console.error('SignageApplication destroyed with error:', error);
                reject(error);
            }
        });
        return this.destroyPromise;
    }
    //#endregion
    //#region Handlers
    handleStatus(statusData) {
        if (this.lastStatusFromContentSyncManager !== statusData.code) {
            this.logger.debug('[SignageApplication] Received status from ContentSyncManager', statusData);
        }
        this.lastStatusFromContentSyncManager = statusData.code;
        if (statusData.code === SyncStatusCode.Unauthorized) {
            this.logger.warning('[SignageApplication] Unauthorized access detected. Forcing reinit.');
            this.lifecycle.reboot();
            return;
        }
        if (statusData.code === SyncStatusCode.ConnectionRefused) {
            if (this.offlineMode) {
                return;
            }
            this.logger.info('[SignageApplication] Connection refused.');
            this.enterOfflineMode();
            return;
        }
        if (statusData.code === SyncStatusCode.ConnectionEstablished) {
            if (!this.offlineMode) {
                return;
            }
            this.logger.info('[SignageApplication] Connection established. Exiting offline mode.');
            this.exitOfflineMode();
            return;
        }
        this.logger.warning('[SignageApplication] Unhandled status code received: ' + statusData.code);
    }
    /**
     * @param {boolean} isFromCache - if true, the event list is from cache and not from the server (emergency generated event list)
     */
    handleNewEventList(eventList, isFromCache = false) {
        const validationResult = validateEventListResponseForAllDisplays(eventList);
        if (!validationResult.ok) {
            this.logger.error(`[SignageApplication] Rejecting malformed event list payload: ${validationResult.error}`, { isFromCache });
            return;
        }
        if (!isFromCache && this.offlineMode) {
            this.exitOfflineMode();
        }
        if (!isFromCache) {
            this.logger.debug('[SignageApplication] New event list received from server', {
            /*eventList: eventList*/
            });
        }
        this.eventList = eventList;
        // Evict download-failure backoff for playables no longer in the event list so the map can't grow
        // unbounded; a freshly (re)published playable starts with a clean slate and retries promptly.
        // Membership is by playable id — `playables` is a PlayableItem[] keyed by position, so indexing
        // it with the (sparse) playable id would wrongly evict still-present playables.
        const presentPlayableIds = new Set(eventList.playables.map(p => p.id));
        for (const playableId of Array.from(this.downloadFailureBackoff.keys())) {
            if (!presentPlayableIds.has(playableId)) {
                this.downloadFailureBackoff.delete(playableId);
            }
        }
        if (this.device) {
            this.eventListLoadedForDisplayIds = this.device.displays.map(display => display.id);
        }
        else {
            this.eventListLoadedForDisplayIds = eventList.event_lists.map(eventList => eventList.display_ids).flat();
        }
        this.evaluateEventListCoverage(eventList, isFromCache);
    }
    /**
     * Detect when a received event list does not contain entries for every
     * display the device currently has. This happens when the backend's
     * EL-generation queue hasn't produced output for a freshly-paired display
     * yet (the API/WS still serves the previous, incomplete payload). Without
     * intervention we'd wait until the next regular poll (1h in WS mode), so
     * arm a fast retry that hits the API every minute until the EL catches up.
     */
    evaluateEventListCoverage(eventList, isFromCache) {
        if (!this.device || this.device.displays.length === 0) {
            return;
        }
        const elDisplayIds = new Set(eventList.event_lists.flatMap(el => el.display_ids));
        const missingDisplayIds = this.device.displays.map(d => d.id).filter(id => !elDisplayIds.has(id));
        if (missingDisplayIds.length === 0) {
            this.cancelIncompleteEventListRetry();
            return;
        }
        this.logger.notice('[SignageApplication] Received event list does not cover every display — backend EL generation likely still pending', {
            missingDisplayIds,
            isFromCache,
        });
        if (!isFromCache) {
            this.scheduleIncompleteEventListRetry();
        }
    }
    scheduleIncompleteEventListRetry() {
        if (this.incompleteEventListRetryTimer) {
            return;
        }
        this.incompleteEventListRetryTimer = setTimeout(() => {
            this.incompleteEventListRetryTimer = null;
            if (this.offlineMode) {
                // No point hitting the API while offline; once we come back online and
                // a fresh EL arrives, evaluateEventListCoverage will re-arm this if needed.
                return;
            }
            this.apiService
                .getEventlistsForAllDisplays()
                .then(eventList => this.handleNewEventList(eventList))
                .catch(error => {
                this.logger.warning('[SignageApplication] Error fetching eventlist during incomplete-EL retry', { error });
                this.scheduleIncompleteEventListRetry();
            });
        }, jitter(SignageApplication.INCOMPLETE_EL_RETRY_MS));
    }
    cancelIncompleteEventListRetry() {
        if (this.incompleteEventListRetryTimer) {
            clearTimeout(this.incompleteEventListRetryTimer);
            this.incompleteEventListRetryTimer = null;
        }
    }
    /**
     * @param {boolean} isFromCache - if true, the device data is from cache and not from the server
     */
    // Default assumes the app drives displays[0]; platforms that bind another
    // display (web URL selection) override with a mere-existence check.
    shouldRelaunchForDisplayChange(device) {
        return (this.displayPlayerManagerInitializedOnDisplay !== null &&
            device.displays.length > 0 &&
            this.displayPlayerManagerInitializedOnDisplay !== device.displays[0].id);
    }
    async handleNewDeviceData(device, isFromCache = false) {
        const validationResult = validateDevice(device);
        if (!validationResult.ok) {
            this.logger.error(`[SignageApplication] Rejecting malformed device payload: ${validationResult.error}`, {
                isFromCache,
                device: sanitizeForLogging(device),
            });
            return;
        }
        this.device = device;
        if (this.displayPlayerManagerInitializedOnDisplay === null && device.displays.length > 0) {
            await this.initializeDisplayPlayerManager(device);
        }
        // if displays changed and we do not have event list loaded for all displays, we need to fetch it again
        if (this.device &&
            this.device.displays.length !== this.eventListLoadedForDisplayIds.length &&
            this.eventListLoadedForDisplayIds.every(displayId => device.displays.some(display => display.id === displayId))) {
            this.apiService
                .getEventlistsForAllDisplays()
                .then(eventList => {
                this.handleNewEventList(eventList);
            })
                .catch(error => {
                this.logger.warning('[SignageApplication] Error re-fetching eventlists after display change', { error });
            });
        }
        if (this.shouldRelaunchForDisplayChange(device)) {
            // probably the best solution is to relaunch the application - edge case
            await this.lifecycle.relaunch();
            return;
            //await this.displayPlayerManager.changeDisplay(this.displayPlayerManagerInitializedOnDisplay, device.displays[0].id);
            //this.schedulerForDisplay.delete(this.displayPlayerManagerInitializedOnDisplay);
            //this.schedulerForDisplay.set(device.displays[0].id, new Scheduler(this.logger));
            //this.logger.debug('[SignageApplication] Display player manager changed display', { oldDisplayId: this.displayPlayerManagerInitializedOnDisplay, newDisplayId: device.displays[0].id });
            //this.displayPlayerManagerInitializedOnDisplay = device.displays[0].id;
        }
        if (!isFromCache && this.offlineMode) {
            this.exitOfflineMode();
        }
        this.logger.debug('[SignageApplication] New device data received', sanitizeForLogging({ device }));
        // Display set may have changed — re-evaluate whether the current EL still covers every display.
        if (this.eventList) {
            this.evaluateEventListCoverage(this.eventList, false);
        }
        await this.screenService.processDeviceData(device, false);
        // Propagate display set changes immediately so imminence tracking prunes
        // stale displays before the next mainLoop tick.
        this.flushScreenContentImminence();
        if (this.multiInstanceManager) {
            await this.multiInstanceManager.processDeviceData(device, true);
        }
        if (!isFromCache) {
            // do not process actions if the device data is from cache (could result in infinite loop)
            await this.processActionService.handleNewDeviceData(device);
        }
        await this.healthcheckManager.ping(HealthcheckType.DeviceDataReceived);
    }
    /**
     * Immediate full screen + multi-instance pass on a display hotplug event (platform
     * watcher). Events are a trigger, not data: screens are re-read fresh through the
     * screen service, and the periodic tick remains the guaranteed fallback — so a missed
     * event costs nothing and a spurious one is idempotent.
     */
    async handleDisplayTopologyChange() {
        if (!this.device)
            return;
        this.logger.notice('[SignageApplication] Display topology changed — running immediate screen pass');
        try {
            await this.screenService.getScreens(true);
            await this.screenService.processDeviceData(this.device, true);
            if (this.multiInstanceManager) {
                await this.multiInstanceManager.processDeviceData(this.device, true);
            }
        }
        catch (error) {
            this.logger.warning('[SignageApplication] Immediate screen pass after display topology change failed', { error: error });
        }
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    handleLowBandwidthModeChange(_enabled) {
        // Override in subclass to react to low bandwidth mode changes
    }
    handleNewTimeData(currentTime) {
        if (this.offlineMode) {
            this.exitOfflineMode();
        }
        this.logger.debug('[SignageApplication] New time data received', { currentTime });
        const now = new Date();
        const currentTimeDate = new Date(currentTime.currentTime);
        this.dateDiff = currentTimeDate.getTime() - now.getTime();
        this.logger.debug('[SignageApplication] Syncing time', { serverTime: currentTimeDate.toISOString(), clientTime: now.toISOString(), diff: this.dateDiff });
    }
    //#endregion
    //#region Main (Content) Loop
    mainLoop() {
        if (!this.eventList || !this.device) {
            if (!this.offlineMode) {
                this.logger.debug('[SignageApplication] No event list or device available yet, skipping main loop iteration', {
                    isEventListAvailable: !!this.eventList,
                    isDeviceAvailable: !!this.device,
                });
            }
            return;
        }
        const playables = Object.values(this.eventList.playables);
        const nowMono = monotonicMs();
        for (const playable of playables) {
            if (!this.mediaControl.supportsDownloading(playable))
                continue;
            if (this.mediaControl.isDownloaded(playable)) {
                // Downloaded: the failure episode is over, so a later failure should log fresh.
                this.downloadFailureBackoff.delete(playable.id);
                continue;
            }
            if (this.mediaControl.isDownloading(playable)) {
                // Keep the failure episode while an attempt is in flight. The attempt result will
                // clear the backoff on success or increment it on failure.
                continue;
            }
            // A playable that can't be fetched must not be re-attempted + error-logged every 1s tick
            // (one failing playable previously produced ~900 error captures/hour). Throttle the attempt
            // cadence with exponential backoff capped at 60s, but never give up — keep retrying so
            // content published later still downloads. See registerDownloadFailure.
            const backoff = this.downloadFailureBackoff.get(playable.id);
            if (backoff && nowMono < backoff.nextAttemptMono) {
                continue;
            }
            this.mediaControl
                .downloadMedia(playable, false)
                .then(result => {
                if (result.code === DownloadResultCode.SUCCESS) {
                    this.downloadFailureBackoff.delete(playable.id);
                    this.logger.info(`[SignageApplication] Playable ${playable.id} downloaded successfully`);
                }
                else if (result.code === DownloadResultCode.ERROR) {
                    this.registerDownloadFailure(playable.id, result.error ?? 'Unknown error');
                }
            })
                .catch(err => {
                this.registerDownloadFailure(playable.id, err);
            });
        }
        const adjustedNow = new Date(Date.now() + (this.dateDiff ?? 0));
        for (const display of this.device.displays) {
            // Compute upcoming items synchronously up-front so we can derive the
            // content-imminence hint without waiting on the async player fetch.
            const upcomingItems = EventListService.getNextItems(this.eventList, adjustedNow, 2, display.id);
            this.displayImminence.set(display.id, this.computeImminence(upcomingItems));
            this.getPlayerForDisplay(display.id)
                .then(player => {
                if (!player) {
                    // player might be not initialized yet - for example screen could be disconnected so player is not initialized and prob this will remain like this for a while so we do not need to log this
                    // this.logger.debug('[SignageApplication] No player available for scheduling', {display: display.id});
                    return;
                }
                if (!this.eventList) {
                    this.logger.warning('[SignageApplication] No event list available for scheduling', { display: display.id });
                    return;
                }
                // this.logger.debug('[SignageApplication] Upcoming items for scheduling', {display: display.id, upcomingItems: upcomingItems.map(item => item.playable.id)});
                const currentlyPlaying = upcomingItems[0] || null;
                const nextItem = upcomingItems[1] || null;
                const isPrepared = (item) => item && (this.mediaControl.isDownloaded(item.playable) || !this.mediaControl.supportsDownloading(item.playable));
                let scheduler = this.schedulerForDisplay.get(display.id);
                if (!scheduler) {
                    scheduler = new Scheduler(this.logger, this.getPlaybackLogger(display.id), display.id);
                    this.schedulerForDisplay.set(display.id, scheduler);
                }
                scheduler.schedule(isPrepared(currentlyPlaying) ? currentlyPlaying : null, isPrepared(nextItem) ? nextItem : null, player, (!isPrepared(currentlyPlaying) || !isPrepared(nextItem)) && (nextItem !== null || currentlyPlaying !== null), this.dateDiff, currentlyPlaying && !isPrepared(currentlyPlaying) ? currentlyPlaying : null);
            })
                .catch(error => {
                this.logger.warning('[SignageApplication] Error in scheduling for display', { displayId: display.id, error });
            });
        }
        this.flushScreenContentImminence();
        if (!this.lastPlayHealthcheckPingMono || monotonicMs() - this.lastPlayHealthcheckPingMono > 1000 * 60) {
            this.healthcheckManager.ping(HealthcheckType.MainLoop);
            this.lastPlayHealthcheckPingMono = monotonicMs();
        }
    }
    //#endregion
    //#region Monitoring Loop
    /**
     * Records a per-playable download failure and schedules the next attempt with exponential
     * backoff capped at 60s. The download is retried indefinitely (never given up) — only the
     * cadence is throttled so a permanently-failing playable can't storm the logs. Only the first
     * failure of an episode logs at error; subsequent throttled retries log at debug.
     */
    registerDownloadFailure(playableId, error) {
        const failures = (this.downloadFailureBackoff.get(playableId)?.failures ?? 0) + 1;
        // 5s → 10s → 20s → 40s → 60s (cap), then steady 60s forever.
        const backoffMs = Math.min(60000, 5000 * Math.pow(2, Math.min(failures - 1, 4)));
        this.downloadFailureBackoff.set(playableId, { failures, nextAttemptMono: monotonicMs() + backoffMs });
        if (failures === 1) {
            this.logger.error(`[SignageApplication] Failed to download playable ${playableId}`, { error: error });
        }
        else {
            this.logger.debug(`[SignageApplication] Still failing to download playable ${playableId}; backing off`, {
                error: error,
                consecutiveFailures: failures,
                retryInMs: backoffMs,
            });
        }
    }
    async monitoringLoop() {
        this.monitoringIndex++;
        if (this.device) {
            this.screenService
                .processDeviceData(this.device, true)
                .then(() => {
                if (this.multiInstanceManager && this.device) {
                    return this.multiInstanceManager.processDeviceData(this.device, false);
                }
            })
                .catch(err => this.logger.warning('[SignageApplication] monitoringLoop device-data processing failed', { err }));
            // restart the screen vendor every 24 hours (monotonic; survives NTP jumps)
            const now = monotonicMs();
            const dueForRestart = !this.lastScreenVendorRestartMono || now - this.lastScreenVendorRestartMono > 1000 * 60 * 60 * 24;
            const backoffElapsed = now >= this.screenVendorRestartNextAttemptMono;
            if (dueForRestart && backoffElapsed) {
                this.logger.debug('[SignageApplication] Restarting screen vendors');
                this.screenVendor
                    .restartScreens(this.device)
                    .then(results => {
                    const failures = results.filter(r => r.error);
                    if (failures.length > 0) {
                        this.screenVendorRestartFailureCount++;
                        // Exponential backoff capped at 1h: 1m → 2m → 4m → 8m → 16m → 32m → 60m.
                        const backoffMs = Math.min(60 * 60000, 60000 * Math.pow(2, Math.min(this.screenVendorRestartFailureCount - 1, 6)));
                        this.screenVendorRestartNextAttemptMono = monotonicMs() + backoffMs;
                        this.logger.warning('[SignageApplication] Some screen vendors failed to restart; backing off', {
                            failures,
                            consecutiveFailures: this.screenVendorRestartFailureCount,
                            retryInMs: backoffMs,
                        });
                        return; // don't update timestamp -> retry after backoff
                    }
                    this.screenVendorRestartFailureCount = 0;
                    this.screenVendorRestartNextAttemptMono = 0;
                    this.lastScreenVendorRestartMono = monotonicMs();
                })
                    .catch(error => {
                    this.logger.error('[SignageApplication] restartScreens rejected unexpectedly', { error });
                });
            }
        }
        this.mediaControl.checkStuckDownloads(false);
        if (this.monitoringIndex % 7 === 0) {
            // every 7 minutes
            this.mediaControl
                .verifyDownloadedMediaIntegrity()
                .then(evicted => {
                if (evicted > 0) {
                    this.logger.notice('[SignageApplication] Media integrity check evicted entries, will re-download on next main loop', { evicted });
                }
            })
                .catch((error) => this.logger.error('[SignageApplication] Error verifying media integrity', { error: error }));
        }
        if (this.monitoringIndex % 11 === 0 && !this.device?.low_bandwidth_mode) {
            // every 11 minutes
            // use prime number to run only once a while and do not colidate with the other checks
            this.logger.debug('[SignageApplication] Checking for unused medias');
            this.mediaControl
                .checkForUnusedMedias(Object.values(this.eventList?.playables || {}), true)
                .catch((error) => this.logger.error('[SignageApplication] Error checking for unused medias', { error: error }));
        }
        if (this.monitoringIndex % 13 === 0) {
            // every 13 minutes
            // use prime number to run only once a while and do not colidate with the other checks
            this.logger.debug('[SignageApplication] Checking storage limit');
            this.mediaControl
                .checkStorageLimit(Object.values(this.eventList?.playables || {}), this.config.storage.limit)
                .catch((error) => this.logger.error('[SignageApplication] Error checking storage limit', { error: error }));
        }
        if (this.monitoringIndex % 17 === 0) {
            // every 17 minutes
            this.schedulerForDisplay.forEach((scheduler, displayId) => {
                if (this.device && !this.device.displays.find(d => d.id === displayId)) {
                    this.schedulerForDisplay.delete(displayId);
                }
            });
        }
        if (this.monitoringIndex % 2 === 0) {
            // every 2 minutes
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            this.logger.flush(10000).catch(() => { });
        }
        if (!this.lastAppStatusLogMono || monotonicMs() - this.lastAppStatusLogMono > 1000 * 60 * 60 * 12) {
            this.logAppStatus();
            this.lastAppStatusLogMono = monotonicMs();
        }
        const deviceContext = await this.updateLoggerAdditionalContext();
        const deviceInfo = await this.deviceService.getDeviceInfo();
        const { screen_list, is_multiple_screens } = buildScreenListPayload(await this.screenService.getScreens(false));
        const deviceUpdateData = {
            mac: deviceInfo.mac ?? undefined,
            actual_datetime: new Date().toISOString(),
            time_from_start: Math.floor((new Date().getTime() - this.config.startTime.getTime()) / 1000), // in seconds
            app_version: this.config.version,
            platform: this.platform,
            last_start: this.config.startTime.toISOString(),
            screen_list,
            is_multiple_screens,
            manufacturer: deviceInfo.manufacturer ?? undefined,
            cpu_name: deviceInfo.cpuName ?? undefined,
            product_name: deviceInfo.productName ?? undefined,
            ram: deviceInfo.ram ?? undefined,
            disk_usage: Math.round((deviceContext?.storage_size && deviceContext?.free_storage
                ? ((deviceContext.storage_size - deviceContext.free_storage) / deviceContext.storage_size) * 100
                : 0) * 10) / 10,
            ...this.getAdditionalDeviceData(),
        };
        if (this.config.localIp && this.config.localIp !== '127.0.0.1') {
            // in some cases local ip is detected as 127.0.0.1, which is not correct and not usefull at all
            deviceUpdateData.local_ip = this.config.localIp;
        }
        const publicIp = await this.config.publicIp;
        if (publicIp !== 'OFFLINE') {
            deviceUpdateData.public_ip = publicIp;
        }
        if (this.shouldReportDeviceData()) {
            this.apiService
                .updateDevice(deviceUpdateData)
                /*.then(() => {
                  // TODOv1.1 could be used if we encountered issue with healthcheck pinging, otherwise remove this
                  this.healthcheckManager.ping(HealthcheckType.DeviceDataReceived).catch(() => {});
                })*/
                .catch(error => {
                const errorSummary = this.summarizeRequestError(error);
                if (isNetworkError(error)) {
                    this.logger.notice('[SignageApplication] Error updating device data', { error: errorSummary });
                }
                else {
                    this.logger.warning('[SignageApplication] Error updating device data', { error: errorSummary });
                }
            });
        }
        // reset monitoring index if too high
        if (this.monitoringIndex > 1000) {
            this.monitoringIndex = 0;
        }
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getPlaybackLogger(displayId) {
        return null;
    }
    shouldReportDeviceData() {
        return true;
    }
    getAdditionalDeviceData() {
        return {};
    }
    //#endregion
    //#region Offline Mode
    isOffline() {
        return this.offlineMode;
    }
    enterOfflineMode() {
        this.logger.info('[SignageApplication] Entering offline mode.');
        this.offlineMode = true;
        this.offlineSince = Date.now();
        this.offlineSinceMono = monotonicMs();
        this.generateEmergencyData();
        if (this.emergencyGeneratorInterval) {
            clearInterval(this.emergencyGeneratorInterval);
        }
        this.emergencyGeneratorInterval = setInterval(() => {
            this.generateEmergencyData();
        }, 1000 * 60 * 15); // 15 minutes
        this.updateLoggerAdditionalContext();
    }
    exitOfflineMode() {
        if (!this.offlineMode) {
            return;
        }
        // Use monotonic clock for duration so an NTP jump while we were offline
        // can't produce a negative or absurd duration in the report.
        const offlineDurationSeconds = this.offlineSinceMono !== null ? Math.floor((monotonicMs() - this.offlineSinceMono) / 1000) : null;
        const offlineSinceIso = this.offlineSince !== null ? new Date(this.offlineSince).toISOString() : null;
        this.logger.info('[SignageApplication] Exiting offline mode.', { offlineSince: offlineSinceIso, offlineDurationSeconds });
        this.offlineMode = false;
        this.offlineSince = null;
        this.offlineSinceMono = null;
        if (this.emergencyGeneratorInterval) {
            clearInterval(this.emergencyGeneratorInterval);
            this.emergencyGeneratorInterval = null;
        }
        this.updateLoggerAdditionalContext();
    }
    generateEmergencyData() {
        if (!this.offlineMode) {
            return;
        }
        this.cacheManager
            .tag(CacheStoreTag.Persistent)
            .get(CacheKey.EventList)
            .then(eventList => {
            if (!this.offlineMode) {
                this.logger.debug('[SignageApplication] Skipping emergency event list generation — offline mode exited while reading cache');
                return;
            }
            const isDebug = this.config.emergencyEl.debug;
            const debugLogger = isDebug ? this.logger : null;
            const result = EventListService.generateEmergencyEventList(eventList, this.eventList, debugLogger);
            if (!result) {
                this.logger.debug('[SignageApplication] No cached data available for emergency event list generation');
                return;
            }
            if (result.action === 'generated') {
                this.handleNewEventList(result.eventList, true);
                this.logger.debug('[SignageApplication] Emergency event list generated', {
                    generatedItems: result.debug.generatedItemsCount,
                    coveredUntil: result.debug.coveredUntil,
                });
            }
            else if (!this.eventList) {
                // Offline start (power cut, reboot without network): the cached server list still covers
                // the next hours, but nothing loaded it into this process — without this the screens stayed
                // empty until the network came back or the cached list ran out (bench Pi 5, 2026-10-06).
                this.handleNewEventList(result.eventList, true);
                this.logger.notice('[SignageApplication] Offline start: playing the cached server event list', {
                    coveredUntil: result.debug.coveredUntil,
                });
            }
            else {
                this.logger.debug('[SignageApplication] Server event list still valid, skipping emergency generation');
            }
            if (isDebug) {
                this.cacheManager.tag(CacheStoreTag.Persistent).set(CacheKey.EmergencyELDebug, {
                    timestamp: new Date().toISOString(),
                    ...result.debug,
                });
            }
        })
            .catch(error => {
            this.logger.warning('[SignageApplication] Error reading cached eventlist for emergency data', { error });
        });
        this.cacheManager
            .tag(CacheStoreTag.Persistent)
            .get(CacheKey.DeviceData)
            .then(device => {
            if (!this.offlineMode) {
                this.logger.debug('[SignageApplication] Skipping cached device data restore — offline mode exited while reading cache');
                return;
            }
            if (device) {
                this.handleNewDeviceData(device, true);
            }
        })
            .catch(error => {
            this.logger.warning('[SignageApplication] Error reading cached device data for emergency data', { error });
        });
    }
    //#endregion
    //#region Helper Functions
    logAppStatus() {
        const uptimeSeconds = Math.floor((Date.now() - this.config.startTime.getTime()) / 1000);
        const schedulerSummaries = {};
        this.schedulerForDisplay.forEach((scheduler, displayId) => {
            schedulerSummaries[displayId] = scheduler.getStatusSummary();
        });
        this.logger.notice('[SignageApplication] Periodic status report', {
            offlineMode: this.offlineMode,
            hasDevice: !!this.device,
            hasEventList: !!this.eventList,
            displayCount: this.device?.displays?.length ?? 0,
            displayIds: this.device?.displays?.map(d => d.id) ?? [],
            schedulers: schedulerSummaries,
            dateDiff: this.dateDiff,
            lastSyncStatus: this.lastStatusFromContentSyncManager,
            uptimeSeconds,
            appVersion: this.config.version,
            downloadedMediaCount: this.mediaControl.getDownloadedMedias().length,
            isDownloading: this.mediaControl.isDownloadingSomething(),
            eventListLoadedForDisplayIds: this.eventListLoadedForDisplayIds,
            lowBandwidthMode: this.device?.low_bandwidth_mode ?? false,
        });
    }
    summarizeRequestError(error) {
        const err = error;
        const summary = {
            name: err?.name,
            message: err?.message,
            code: err?.code,
        };
        const status = err?.response?.status;
        if (status !== undefined)
            summary.status = status;
        return summary;
    }
    async updateLoggerAdditionalContext() {
        try {
            const deviceContext = {
                online: !this.isOffline(),
                device_unique_identifier: this.authInstance.hash,
            };
            this.logger.setAdditionalContext({
                device: deviceContext,
            });
            return deviceContext;
        }
        catch (error) {
            this.logger.error('[SignageApplication] Error updating logger additional context', { error: error });
            return null;
        }
    }
    async initializeDisplayPlayerManager(device) {
        if (this.displayPlayerManagerInitializedOnDisplay === null && device.displays.length > 0) {
            await this.displayPlayerManager.getOrInitializeForDisplay(device.displays[0].id);
            this.logger.debug('[SignageApplication] Display player manager initialized', { displayId: device.displays[0].id });
            this.displayPlayerManagerInitializedOnDisplay = device.displays[0].id;
        }
    }
    async getPlayerForDisplay(displayId) {
        if (this.displayPlayerManager.hasForDisplay(displayId)) {
            return await this.displayPlayerManager.getOrInitializeForDisplay(displayId);
        }
        return null;
    }
    /**
     * Decide whether a display has content playing now or starting within the
     * configured wake-up threshold. Returns `true` (safe default) when the
     * content-idle feature is disabled, so callers can treat the result as
     * "should screens be on for this display".
     */
    computeImminence(items) {
        const thresholdSec = this.config.screens.idleWakeupSeconds;
        if (thresholdSec === null)
            return true; // feature disabled — always report imminent
        const now = Date.now() + (this.dateDiff ?? 0);
        for (const item of items) {
            const fromMs = new Date(item.from).getTime();
            const toMs = new Date(item.to).getTime();
            if (fromMs <= now && now <= toMs)
                return true; // currently playing
            if (fromMs > now && fromMs - now <= thresholdSec * 1000)
                return true; // imminent
        }
        return false;
    }
    /**
     * Aggregate per-display imminence and publish the hint to ScreenService.
     * Multi-display OR: if any display has imminent content, keep screens on.
     * No-op when the feature is disabled in config.
     */
    flushScreenContentImminence() {
        if (this.config.screens.idleWakeupSeconds === null)
            return;
        if (!this.device)
            return;
        const activeIds = new Set(this.device.displays.map(d => d.id));
        for (const id of Array.from(this.displayImminence.keys())) {
            if (!activeIds.has(id))
                this.displayImminence.delete(id);
        }
        // Don't propagate `false` until mainLoop has actually computed imminence
        // for at least one active display. Without this guard, the call to
        // flushScreenContentImminence in handleNewDeviceData runs before the first
        // mainLoop tick has populated displayImminence, so anyImminent is false
        // purely because the map is empty — which clears the optimistic-bootstrap
        // `contentImminent = true` in ScreenService and can cause an unwanted
        // turnOffScreens during business hours at startup.
        if (this.displayImminence.size === 0)
            return;
        let anyImminent = false;
        this.displayImminence.forEach(v => {
            if (v)
                anyImminent = true;
        });
        this.screenService.updateContentImminence(anyImminent).catch(error => {
            this.logger.warning('[SignageApplication] updateContentImminence failed', { error });
        });
    }
}
SignageApplication.INCOMPLETE_EL_RETRY_MS = 60 * 1000;
//# sourceMappingURL=SignageApplication.js.map